import { createTheme, Theme as MUITheme, Palette } from "@mui/material/styles";
import { Theme } from 'olympe';
import {getLogger} from '@olympeio/core';


/**
 * Create once an empty theme that can be returned whenever we don't have a theme in context
 * @type {MUITheme}
 */
export const emptyTheme = createTheme({});


/**
 * Transforms a Theme CloudObject in a MUI Theme object
 * @param { Theme } theme
 * @return { MUITheme }
 */
export const getMuiTheme = (theme) => {
    const logger = getLogger('MUI Theme');
    if (theme instanceof Theme) {
        /**
         * @param {string} prop
         * @return {{main: string | undefined}}
         */
        const getPaletteColorDefinition = (prop) => {
            const color = theme.get(prop);
            return color ? {main: theme.get(prop)?.toHexString()} : undefined;
        };

        const colorValues = {
            primary: getPaletteColorDefinition(Theme.primaryColorProp),
            secondary: getPaletteColorDefinition(Theme.secondaryColorProp),
            error: getPaletteColorDefinition(Theme.errorColorProp),
            warning: getPaletteColorDefinition(Theme.warningColorProp),
            info: getPaletteColorDefinition(Theme.infoColorProp),
            success: getPaletteColorDefinition(Theme.successColorProp),
        };

        /**
         * @type {Partial<Palette>}
         */
        const palette = Object.keys(colorValues).reduce((p, key) => {
            if (colorValues[key]) {
                p[key] = colorValues[key];
            }
            return p;
        }, {});

        const fontFamily = theme.get(Theme.fontFamilyProp);
        const typography = {
                fontFamily: fontFamily ?? 'system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue",Arial',
                h1: {
                    fontSize: 34,
                },
                h2: {
                    fontSize: 30,
                },
                h3: {
                    fontSize: 26,
                },
                h4: {
                    fontSize: 22,
                },
                h5: {
                    fontSize: 18,
                },
                h6: {
                    fontSize: 16,
                },
                body1: {
                    fontSize: 16,
                },
                body2: {
                    fontSize: 16,
                }
            }

        return createTheme({palette, typography});
    } else {
        logger.warn(`Invalid theme used in getMuiTheme(): ${theme}`);
        return emptyTheme;
    }
}



/**
 * Filter an array of objects whose any properties contains a value
 * https://stackoverflow.com/questions/44312924/filter-array-of-objects-whose-any-properties-contains-a-value
 * @param {!Array<!Object>} array
 * @param {string} text
 * @return {!Array<Object>}
 */
export function filterByValue(array, text) {
    const keysToOmit = ['tag', 'filterable', 'editable'];

    return array.filter(item => {
        const filteredArray = Object.keys(item).filter(key => {
            return !keysToOmit.includes(key.toLowerCase());
        });

        return filteredArray.some(key => {
            const isIncluded = item[key]?.toString().toLowerCase().includes(text.toLowerCase());
            return !(item[key] instanceof Date) && isIncluded;
        });
    });
}



/**
 * Coming from : https://stackoverflow.com/questions/5560248/programmatically-lighten-or-darken-a-hex-color-or-rgb-and-blend-colors
 *
 * @param p number between -1 and 1 percentage to lighten or darken the color or indicate the blending rate if 2 colors provided
 * @param c0 First color
 * @param c1 Second color to blend with
 * @param l boolean: can be logarithmic blend or linear blend
 * @returns {string|null}
 */
export function pSBC(p,c0,c1,l) {
    let r,g,b,P,f,t,h,i=parseInt,m=Math.round,a=typeof(c1)=="string";
    if(typeof(p)!="number"||p<-1||p>1||typeof(c0)!="string"||(c0[0]!='r'&&c0[0]!='#')||(c1&&!a))return null;
    const pSBCr=(d)=>{
        let n=d.length,x={};
        if(n>9){
            [r,g,b,a]=d=d.split(","),n=d.length;
            if(n<3||n>4)return null;
            x.r=i(r[3]=="a"?r.slice(5):r.slice(4)),x.g=i(g),x.b=i(b),x.a=a?parseFloat(a):-1
        }else{
            if(n==8||n==6||n<4)return null;
            if(n<6)d="#"+d[1]+d[1]+d[2]+d[2]+d[3]+d[3]+(n>4?d[4]+d[4]:"");
            d=i(d.slice(1),16);
            if(n==9||n==5)x.r=d>>24&255,x.g=d>>16&255,x.b=d>>8&255,x.a=m((d&255)/0.255)/1000;
            else x.r=d>>16,x.g=d>>8&255,x.b=d&255,x.a=-1
        }return x};
    h=c0.length>9,h=a?c1.length>9?true:c1=="c"?!h:false:h,f=pSBCr(c0),P=p<0,t=c1&&c1!="c"?pSBCr(c1):P?{r:0,g:0,b:0,a:-1}:{r:255,g:255,b:255,a:-1},p=P?p*-1:p,P=1-p;
    if(!f||!t)return null;
    if(l)r=m(P*f.r+p*t.r),g=m(P*f.g+p*t.g),b=m(P*f.b+p*t.b);
    else r=m((P*f.r**2+p*t.r**2)**0.5),g=m((P*f.g**2+p*t.g**2)**0.5),b=m((P*f.b**2+p*t.b**2)**0.5);
    a=f.a,t=t.a,f=a>=0||t>=0,a=f?a<0?t:t<0?a:a*P+t*p:0;
    if(h)return"rgb"+(f?"a(":"(")+r+","+g+","+b+(f?","+m(a*1000)/1000:"")+")";
    else return"#"+(4294967296+r*16777216+g*65536+b*256+(f?m(a*255):0)).toString(16).slice(1,f?undefined:-2)
}